"use client";

import React from "react";
import { Mic2, Waves, Disc } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const standards = [{
  id: "recording",
  label: "CAPTURE",
  title: "High-Fidelity Recording",
  description: "Pristine signal paths and world-class acoustics capture every nuance of your performance with absolute clarity and warmth.",
  icon: Mic2,
  animateIcon: false
}, {
  id: "mixing",
  label: "BALANCE",
  title: "Sonic Architecture",
  description: "Our engineers sculpt your sound using a hybrid of analog warmth and digital precision to create a deep, immersive soundstage.",
  icon: Waves,
  animateIcon: true
}, {
  id: "mastering",
  label: "POLISH",
  title: "Final Translation",
  description: "The ultimate refinement process ensuring your music translates perfectly across every playback system, from clubs to earbuds.",
  icon: Disc,
  animateIcon: false
}];
export default function AuthenticityAndPrivacyStandards() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    rootMargin: "0px 0px -100px 0px"
  });
  return <section data-section-id="222399" id="precision-engineering" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} />
      
      <div className="bg-primary/10 absolute top-0 left-1/2 -z-10 h-[500px] w-[1000px] -translate-x-1/2 rounded-full blur-[120px]" />

      <div ref={ref} className={`container mx-auto px-6 transition-all duration-1000 ease-out md:px-12 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
        <div className="mb-16 text-center">
          <h2 className="text-foreground font-default mb-4 text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl uppercase">
            Precision Engineering
          </h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg font-default">
            Where technical mastery meets artistic vision. We provide the tools and expertise to define your signature sound.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {standards.map((standard, i) => {
          const Icon = standard.icon;
          return <div key={standard.id} data-index={i} className={`transition-all duration-700 ease-out`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="bg-card/40 shadow-3xl border border-border/50 backdrop-blur-xl h-full transition-transform duration-300 hover:-translate-y-2 hover:border-primary/50 group">
                  <CardHeader className="pb-4">
                    <div className="mb-6 flex items-center justify-between">
                      <div className={`bg-primary/10 text-primary flex size-16 items-center justify-center rounded-2xl shadow-[0_0_20px_rgba(0,255,255,0.1)] group-hover:shadow-[0_0_30px_rgba(0,255,255,0.3)] transition-all ${standard.animateIcon ? "animate-pulse" : ""}`}>
                        <Icon className="size-8" strokeWidth={1.5} />
                      </div>
                      <span className="text-primary font-mono text-xs font-semibold tracking-widest uppercase">
                        {standard.label}
                      </span>
                    </div>
                    <CardTitle className="font-default text-2xl tracking-tight">
                      {standard.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-base leading-relaxed font-default">
                      {standard.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}